import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Layers, Wrench, Building2, ChevronLeft, ChevronRight } from 'lucide-react';

/*
  HeroVideoCarousel
  Full-width auto-playing hero with 3 CSS/SVG-animated scenes in a continuous loop.
  Layout:
   - Desktop: copy on the left half, animated visual on the right half.
   - Mobile:  copy on top, animated visual behind with reduced opacity so text stays legible.
*/

const DURATION_MS = 9000; // per scene

const SCENES = [
  {
    id: 'platform',
    tag: 'The Platform',
    title: 'Observe. Understand. Automate.',
    subtitle: 'Unified telemetry, a live context graph, and AI agents that act — the operational core of MetaxuOne.',
    cta: { label: 'Explore the Platform', href: '/platform' },
    icon: Layers,
  },
  {
    id: 'services',
    tag: 'Our Services',
    title: 'From assessment to transformation.',
    subtitle: 'Eight stages — Assess, Design, Build, Integrate, Deploy, Operate, Optimize, Transform.',
    cta: { label: 'See all Services', href: '/services/assess' },
    icon: Wrench,
  },
  {
    id: 'industries',
    tag: 'Industry coverage',
    title: 'Purpose-built for regulated sectors.',
    subtitle: 'BFSI, Enterprise, Pharma, Manufacturing, and Government — with the guardrails each demands.',
    cta: { label: 'View Industries', href: '/industries/bfsi' },
    icon: Building2,
  },
];

const HeroVideoCarousel = () => {
  const [active, setActive] = useState(0);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const startedAt = useRef(Date.now());
  const rafRef = useRef(null);

  useEffect(() => {
    startedAt.current = Date.now();
    setProgress(0);
    if (paused) return;
    const tick = () => {
      const elapsed = Date.now() - startedAt.current;
      const p = Math.min(1, elapsed / DURATION_MS);
      setProgress(p);
      if (p >= 1) {
        setActive((a) => (a + 1) % SCENES.length);
      } else {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [active, paused]);

  const goto = (i) => setActive(((i % SCENES.length) + SCENES.length) % SCENES.length);
  const next = () => goto(active + 1);
  const prev = () => goto(active - 1);

  const scene = SCENES[active];
  const Icon = scene.icon;

  return (
    <section
      data-testid="hero-video-carousel"
      className="relative w-full bg-black overflow-hidden border-b border-neutral-900"
      aria-roledescription="carousel"
    >
      <style>{keyframesCSS}</style>

      {/* Container height: tighter on mobile so it fits above the fold */}
      <div className="relative w-full min-h-[500px] h-[68svh] max-h-[600px] sm:h-[70svh] sm:max-h-[640px] lg:min-h-[600px] lg:h-[calc(100vh-4rem)] lg:max-h-[780px] overflow-hidden">
        {/* Scenes background (right half on desktop, dimmed backdrop on mobile) */}
        {SCENES.map((s, i) => (
          <div
            key={s.id}
            className={`absolute inset-0 transition-opacity duration-[900ms] ease-out ${
              i === active ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            aria-hidden={i !== active}
          >
            {i === 0 && <PlatformScene active={i === active} />}
            {i === 1 && <ServicesScene active={i === active} />}
            {i === 2 && <IndustriesScene active={i === active} />}
          </div>
        ))}

        {/* Mobile-only decorative pulse (right-bottom) — color per scene */}
        <div
          aria-hidden
          className="lg:hidden absolute right-6 bottom-24 w-40 h-40 rounded-full pointer-events-none opacity-70 anim-mobile-pulse transition-colors duration-700"
          style={{
            background:
              active === 0 ? 'radial-gradient(circle, rgba(59,130,246,0.55), rgba(59,130,246,0) 65%)'
              : active === 1 ? 'radial-gradient(circle, rgba(6,182,212,0.55), rgba(6,182,212,0) 65%)'
              : 'radial-gradient(circle, rgba(16,185,129,0.55), rgba(16,185,129,0) 65%)',
          }}
        />

        {/* Legibility gradient — heavier on the left (behind copy) */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(90deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.55) 42%, rgba(0,0,0,0.1) 70%, rgba(0,0,0,0) 100%)',
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none lg:hidden"
          style={{
            background: 'linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.35) 50%, rgba(0,0,0,0.7) 100%)',
          }}
        />

        {/* Copy — left half on desktop, top on mobile */}
        <div className="relative z-10 h-full w-full max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10 flex items-center overflow-hidden">
          <div className="w-full min-w-0 max-w-full lg:w-1/2 pr-0 lg:pr-8 pt-6 lg:pt-0">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-black/60 backdrop-blur border border-white/10">
              <Icon className="w-3.5 h-3.5 text-blue-300" />
              <span className="text-[11px] sm:text-[11.5px] uppercase tracking-[0.16em] text-blue-200">{scene.tag}</span>
            </div>
            <h2
              key={scene.id + '-title'}
              className="mt-4 text-[24px] sm:text-[34px] md:text-[44px] lg:text-[54px] xl:text-[60px] leading-[1.06] tracking-[-0.028em] font-semibold text-white anim-fade-up break-words"
            >
              {scene.title}
            </h2>
            <p
              key={scene.id + '-sub'}
              className="mt-3 sm:mt-4 text-[13.5px] sm:text-[15.5px] lg:text-[17px] text-neutral-200/95 leading-relaxed max-w-lg anim-fade-up-delay"
            >
              {scene.subtitle}
            </p>
            <div className="mt-5 sm:mt-6 flex flex-wrap gap-3">
              <Link
                to={scene.cta.href}
                data-testid={`hero-video-cta-${scene.id}`}
                className="h-11 px-5 rounded-full bg-white hover:bg-neutral-200 text-neutral-950 text-[13px] sm:text-[13.5px] font-medium inline-flex items-center gap-2 transition-colors"
              >
                {scene.cta.label} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>

        {/* Prev/Next controls (desktop only) */}
        <div className="absolute z-20 right-6 lg:right-10 top-1/2 -translate-y-1/2 hidden md:flex flex-col gap-2">
          <button
            onClick={prev}
            data-testid="hero-video-prev"
            aria-label="Previous scene"
            className="w-9 h-9 rounded-full bg-black/50 backdrop-blur border border-white/15 text-white hover:bg-black/70 inline-flex items-center justify-center transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={next}
            data-testid="hero-video-next"
            aria-label="Next scene"
            className="w-9 h-9 rounded-full bg-black/50 backdrop-blur border border-white/15 text-white hover:bg-black/70 inline-flex items-center justify-center transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Progress rail */}
        <div className="absolute z-20 bottom-4 sm:bottom-6 left-0 right-0 px-6 lg:px-10">
          <div className="max-w-[1400px] mx-auto flex items-center gap-2 sm:gap-3">
            {SCENES.map((s, i) => {
              const isActive = i === active;
              const w = isActive ? `${Math.max(2, progress * 100)}%` : i < active ? '100%' : '0%';
              return (
                <button
                  key={s.id}
                  onClick={() => goto(i)}
                  data-testid={`hero-video-dot-${s.id}`}
                  aria-label={`Go to ${s.tag}`}
                  className="group flex-1 min-w-0 text-left"
                >
                  <div className={`text-[9.5px] sm:text-[10.5px] uppercase tracking-[0.14em] sm:tracking-[0.16em] mb-1 sm:mb-1.5 truncate transition-colors ${isActive ? 'text-white' : 'text-neutral-500 group-hover:text-neutral-300'}`}>
                    0{i + 1} · {s.tag}
                  </div>
                  <div className="relative h-[2.5px] sm:h-[3px] rounded-full bg-white/10 overflow-hidden">
                    <div className="absolute inset-y-0 left-0 bg-white/85" style={{ width: w, transition: isActive ? 'none' : 'width 400ms ease' }} />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ------------------- Scenes -------------------
   Each scene renders animated visuals in the right half only (viewBox 1200x700).
   All SVG content is positioned in x >= 620 so it never collides with the copy on the left.
   On mobile, scenes get a reduced overall opacity via a CSS class.
*/

const PlatformScene = ({ active }) => (
  <div className="absolute inset-0 bg-gradient-to-br from-[#050b1c] via-[#050914] to-black scene-vis">
    <div
      aria-hidden
      className="absolute inset-0 opacity-[0.35]"
      style={{
        backgroundImage:
          'linear-gradient(to right, rgba(120,160,255,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(120,160,255,0.08) 1px, transparent 1px)',
        backgroundSize: '56px 56px',
        maskImage: 'radial-gradient(ellipse at 75% 45%, black 30%, transparent 80%)',
      }}
    />
    <div
      aria-hidden
      className="absolute inset-0"
      style={{ background: 'radial-gradient(35% 40% at 75% 45%, rgba(59,130,246,0.35), transparent 60%)' }}
    />
    <svg viewBox="0 0 1200 700" className="absolute inset-0 w-full h-full hidden lg:block" preserveAspectRatio="xMidYMid meet">
      <defs>
        <radialGradient id="pf-core-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.9" />
          <stop offset="60%" stopColor="#3b82f6" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="pf-edge-grad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.1" />
          <stop offset="50%" stopColor="#60a5fa" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.1" />
        </linearGradient>
      </defs>

      <circle cx="900" cy="350" r="200" fill="url(#pf-core-glow)" className={active ? 'anim-pulse' : ''} />

      {[110, 160, 210].map((r, i) => (
        <circle
          key={r}
          cx="900" cy="350" r={r}
          fill="none" stroke="rgba(96,165,250,0.35)" strokeWidth="1"
          strokeDasharray="2 6"
          className={active ? `anim-spin-${i}` : ''}
          style={{ transformOrigin: '900px 350px' }}
        />
      ))}

      {/* Orbital nodes — kept in the RIGHT hemisphere only (angles -80..80) */}
      {[
        { a: -70, label: 'Observability' },
        { a: -25, label: 'AI Agents' },
        { a: 0,   label: 'Patch Management' },
        { a: 25,  label: 'Self-Healing' },
        { a: 70,  label: 'Context Graph' },
        { a: -50, label: 'Policy Engine', r: 260 },
        { a: 50,  label: 'Actions',       r: 260 },
      ].map((n, i) => {
        const rad = (n.a * Math.PI) / 180;
        const R = n.r || 210;
        const cx = 900 + Math.cos(rad) * R;
        const cy = 350 + Math.sin(rad) * R;
        return (
          <g key={n.label} className={active ? 'anim-node' : ''} style={{ animationDelay: `${i * 0.35}s` }}>
            <line x1="900" y1="350" x2={cx} y2={cy} stroke="#60a5fa" strokeOpacity="0.45" strokeWidth="1.2" />
            <circle cx={cx} cy={cy} r="16" fill="#0b1020" stroke="#60a5fa" strokeOpacity="0.7" />
            <circle cx={cx} cy={cy} r="5.5" fill="#60a5fa" />
            <text x={cx} y={cy + 34} fontSize="12" fontFamily="Inter, sans-serif" fill="#cbd5e1" textAnchor="middle">
              {n.label}
            </text>
          </g>
        );
      })}

      <g>
        <circle cx="900" cy="350" r="46" fill="#0b1223" stroke="#3b82f6" strokeOpacity="0.8" />
        <circle cx="900" cy="350" r="9" fill="#93c5fd" className={active ? 'anim-core' : ''} />
        <text x="900" y="420" fontSize="12" fontFamily="Inter, sans-serif" fill="#93c5fd" textAnchor="middle" letterSpacing="2">
          UNIFIED PLATFORM
        </text>
      </g>
    </svg>
  </div>
);

const ServicesScene = ({ active }) => {
  const stages = ['Assess', 'Design', 'Build', 'Integrate', 'Deploy', 'Operate', 'Optimize', 'Transform'];
  const pts = [
    [660, 460], [740, 400], [810, 500], [880, 420],
    [945, 340], [1000, 300], [1055, 400], [1105, 380],
  ];
  const pathRef = useRef(null);
  // Capsule starts at Assess (first path point)
  const [pt, setPt] = useState({ x: 640, y: 460 });

  useEffect(() => {
    if (!active) return;
    const path = pathRef.current;
    if (!path) return;
    let total;
    try { total = path.getTotalLength(); } catch (_) { return; }
    // Immediately anchor at path start (Assess)
    const start0 = path.getPointAtLength(0);
    setPt({ x: start0.x, y: start0.y });

    const DUR = 8000; // finish 1s before the 9s slide ends
    let t0 = null;
    let raf;
    const step = (ts) => {
      if (t0 === null) t0 = ts;
      const p = Math.min(1, (ts - t0) / DUR);
      const eased = 1 - Math.pow(1 - p, 2); // ease-out
      const pos = path.getPointAtLength(total * eased);
      setPt({ x: pos.x, y: pos.y });
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [active]);

  return (
    <div className="absolute inset-0 bg-gradient-to-br from-[#02121a] via-[#031222] to-black scene-vis">
      <div
        aria-hidden
        className="absolute inset-0"
        style={{ background: 'radial-gradient(40% 40% at 80% 45%, rgba(6,182,212,0.35), transparent 60%)' }}
      />
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.3]"
        style={{
          backgroundImage:
            'linear-gradient(to right, rgba(100,220,255,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(100,220,255,0.08) 1px, transparent 1px)',
          backgroundSize: '56px 56px',
          maskImage: 'radial-gradient(ellipse at 80% 50%, black 30%, transparent 80%)',
        }}
      />

      <svg viewBox="0 0 1200 700" className="absolute inset-0 w-full h-full hidden lg:block" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="sv-pipe" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.15" />
            <stop offset="50%" stopColor="#22d3ee" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.15" />
          </linearGradient>
          <linearGradient id="sv-stage-fill" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#0e2437" />
            <stop offset="100%" stopColor="#071827" />
          </linearGradient>
        </defs>

        <path
          ref={pathRef}
          d="M 640 460 C 720 340, 790 560, 870 430 S 990 260, 1050 380 S 1120 520, 1130 360"
          fill="none" stroke="url(#sv-pipe)" strokeWidth="3" strokeLinecap="round"
        />

        {stages.map((k, i) => {
          const [cx, cy] = pts[i];
          return (
            <g key={k} className={active ? 'anim-stage' : ''} style={{ animationDelay: `${i * 0.25}s`, transformOrigin: `${cx}px ${cy}px` }}>
              <circle cx={cx} cy={cy} r="22" fill="url(#sv-stage-fill)" stroke="#22d3ee" strokeOpacity="0.8" />
              <text x={cx} y={cy + 4} fontSize="10.5" fontFamily="Inter, sans-serif" fill="#e0f2fe" textAnchor="middle" fontWeight="600">
                {String(i + 1).padStart(2, '0')}
              </text>
              <text x={cx} y={cy + 40} fontSize="11.5" fontFamily="Inter, sans-serif" fill="#cbd5e1" textAnchor="middle" letterSpacing="0.3">
                {k}
              </text>
            </g>
          );
        })}

        {/* Traveling capsule (always visible at current path position) */}
        <g>
          <circle cx={pt.x} cy={pt.y} r="14" fill="#67e8f9" fillOpacity="0.25" />
          <circle cx={pt.x} cy={pt.y} r="7" fill="#67e8f9" />
        </g>
      </svg>
    </div>
  );
};

const IndustriesScene = ({ active }) => {
  // 5 industry pins placed on the RIGHT half only (x >= 640)
  const industries = [
    { k: 'BFSI',          v: 'Banking · Insurance',   x: 720, y: 220, color: '#34d399' },
    { k: 'Enterprise',    v: 'Cross-industry',        x: 880, y: 170, color: '#60a5fa' },
    { k: 'Pharma',        v: 'GxP · Compliance',      x: 1000, y: 260, color: '#f472b6' },
    { k: 'Manufacturing', v: 'OT · Edge',             x: 780, y: 470, color: '#fbbf24' },
    { k: 'Government',    v: 'Sovereign · Regulated', x: 960, y: 500, color: '#22d3ee' },
  ];
  return (
    <div className="absolute inset-0 bg-gradient-to-br from-[#031814] via-[#02120e] to-black scene-vis">
      <div
        aria-hidden
        className="absolute inset-0"
        style={{ background: 'radial-gradient(45% 45% at 75% 45%, rgba(16,185,129,0.28), transparent 60%)' }}
      />

      <svg viewBox="0 0 1200 700" className="absolute inset-0 w-full h-full hidden lg:block" preserveAspectRatio="xMidYMid meet">
        <defs>
          <radialGradient id="in-globe-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#34d399" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#34d399" stopOpacity="0" />
          </radialGradient>
        </defs>

        <circle cx="900" cy="360" r="240" fill="url(#in-globe-glow)" />
        {[220, 180, 140].map((r, i) => (
          <ellipse
            key={r}
            cx="900" cy="360" rx={r} ry={r * 0.42}
            fill="none" stroke="rgba(52,211,153,0.5)" strokeWidth="1" strokeDasharray="2 6"
            className={active ? `anim-spin-${i}` : ''}
            style={{ transformOrigin: '900px 360px' }}
          />
        ))}
        {[220, 180, 140].map((r) => (
          <ellipse
            key={'m' + r}
            cx="900" cy="360" rx={r * 0.42} ry={r}
            fill="none" stroke="rgba(52,211,153,0.25)" strokeWidth="1"
          />
        ))}

        {industries.map((it, i) => (
          <g key={it.k} className={active ? 'anim-node' : ''} style={{ animationDelay: `${0.2 + i * 0.25}s` }}>
            {/* connect to center */}
            <line x1="900" y1="360" x2={it.x} y2={it.y} stroke={it.color} strokeOpacity="0.35" strokeWidth="1" />
            {/* pin */}
            <circle cx={it.x} cy={it.y} r="8" fill={it.color} fillOpacity="0.15" stroke={it.color} strokeOpacity="0.9" />
            <circle cx={it.x} cy={it.y} r="3.5" fill={it.color} />
            {/* label pill */}
            <g transform={`translate(${it.x + 14}, ${it.y - 14})`}>
              <rect x="0" y="0" rx="6" ry="6" width={it.k.length * 8.5 + 14} height="28" fill="#0a0a0a" fillOpacity="0.85" stroke="rgba(255,255,255,0.12)" />
              <text x="8" y="12" fontSize="10.5" fontFamily="Inter, sans-serif" fill="#ffffff" fontWeight="600" dominantBaseline="middle">{it.k}</text>
              <text x="8" y="22" fontSize="9" fontFamily="Inter, sans-serif" fill="#a3a3a3" dominantBaseline="middle">{it.v}</text>
            </g>
          </g>
        ))}
      </svg>
    </div>
  );
};

/* ------------------- Keyframes / CSS ------------------- */
const keyframesCSS = `
@keyframes hv-fadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
.anim-fade-up { animation: hv-fadeUp .7s cubic-bezier(.2,.7,.2,1) both; }
.anim-fade-up-delay { animation: hv-fadeUp .7s cubic-bezier(.2,.7,.2,1) both; animation-delay: .18s; }

@keyframes hv-pulse { 0%,100% { opacity: .55; } 50% { opacity: 1; } }
.anim-pulse { animation: hv-pulse 3.5s ease-in-out infinite; }

@keyframes hv-spinCW { to { transform: rotate(360deg); } }
@keyframes hv-spinCCW { to { transform: rotate(-360deg); } }
.anim-spin-0 { animation: hv-spinCW 28s linear infinite; }
.anim-spin-1 { animation: hv-spinCCW 22s linear infinite; }
.anim-spin-2 { animation: hv-spinCW 18s linear infinite; }

@keyframes hv-node { from { opacity: 0; transform: scale(.6); } to { opacity: 1; transform: scale(1); } }
.anim-node { animation: hv-node .8s cubic-bezier(.2,.9,.3,1) both; transform-origin: center; transform-box: fill-box; }

@keyframes hv-core { 0%,100% { transform: scale(1); opacity: .9; } 50% { transform: scale(1.35); opacity: 1; } }
.anim-core { animation: hv-core 2.6s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }

@keyframes hv-stage { from { opacity: 0; transform: scale(.4); } to { opacity: 1; transform: scale(1); } }
.anim-stage { animation: hv-stage .8s cubic-bezier(.2,.9,.3,1) both; transform-box: fill-box; }

/* On mobile, fade the animated scene so hero copy stays readable */
@media (max-width: 1023px) {
  .scene-vis { opacity: 0.75; }
}

@keyframes hv-mobilePulse { 0%,100% { transform: scale(1); opacity: .55; } 50% { transform: scale(1.15); opacity: .9; } }
.anim-mobile-pulse { animation: hv-mobilePulse 3.6s ease-in-out infinite; }
`;

export default HeroVideoCarousel;
